import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { portfolioStore } from '../../stores/portfolio-store';
import { priceStore } from '../../stores/price-store';
import { Button, Input } from '@privemind/components';
import { AssetConflictModal } from '../asset-conflict-modal/asset-conflict-modal';
import { Asset, AssetConflictData } from '../../types';
import { isError } from '../../lib/utils/type-guards';
import './add-asset-modal.scss';

interface AddAssetModalProps {
  bucketName: string;
  isOpen: boolean;
  onClose: () => void;
}

export const AddAssetModal: React.FC<AddAssetModalProps> = observer(({
  bucketName,
  isOpen,
  onClose
}) => {
  const [assetId, setAssetId] = useState('');
  const [assetAmount, setAssetAmount] = useState('');
  const [assetAvgBuyPrice, setAssetAvgBuyPrice] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [conflictData, setConflictData] = useState<AssetConflictData | null>(null);

  const clearForm = () => {
    setAssetId('');
    setAssetAmount('');
    setAssetAvgBuyPrice('');
    setErrorMessage('');
  };

  const handleClose = () => {
    clearForm();
    onClose();
  };

  const addAsset = async () => {
    const trimmedAssetId = assetId.trim().toLowerCase();
    const amount = parseFloat(assetAmount);
    const avgBuyPrice = assetAvgBuyPrice !== '' ? parseFloat(assetAvgBuyPrice) : undefined;

    setErrorMessage('');

    if (!trimmedAssetId || !amount || amount <= 0) {
      setErrorMessage('Please fill in all fields with valid values');
      return;
    }

    // Check if asset exists in the selected bucket specifically
    if (portfolioStore.assetExistsInBucket(trimmedAssetId, bucketName)) {
      // Asset exists in this bucket - show conflict modal
      const assetInBucketResponse = await priceStore.getAssetDetails(trimmedAssetId);

      if (isError(assetInBucketResponse)) {
        setErrorMessage(`Failed to fetch asset data: ${assetInBucketResponse.message}. Please check the asset ID.`);
        return;
      }

      const pendingData: AssetConflictData = {
        id: trimmedAssetId,
        name: assetInBucketResponse.name,
        symbol: assetInBucketResponse.symbol.toUpperCase(),
        amount: amount,
        bucket: bucketName,
        type: assetInBucketResponse.categories?.includes('decentralized-exchange-tokens') ||
              assetInBucketResponse.categories?.includes('defi') ? 'Token' : 'Coin',
        platforms: Object.keys(assetInBucketResponse.platforms || {}),
        lastUpdated: new Date().toISOString()
      };

      // Add avgBuyPrice if provided
      if (avgBuyPrice !== undefined && !isNaN(avgBuyPrice)) {
        pendingData.avgBuyPrice = avgBuyPrice;
      }

      setConflictData(pendingData);
      return;
    }

    // Asset doesn't exist in this bucket - proceed normally
    const assetResponse = await priceStore.getAssetDetails(trimmedAssetId);

    if (isError(assetResponse)) {
      setErrorMessage(`Failed to add asset: ${assetResponse.message}. Please check the asset ID.`);
      return;
    }

    const assetData: Asset = {
      id: trimmedAssetId,
      name: assetResponse.name,
      symbol: assetResponse.symbol.toUpperCase(),
      amount: amount,
      bucket: bucketName,
      type: assetResponse.categories?.includes('decentralized-exchange-tokens') ||
            assetResponse.categories?.includes('defi') ? 'Token' : 'Coin',
      platforms: Object.keys(assetResponse.platforms || {}),
      lastUpdated: new Date().toISOString()
    };

    // Add avgBuyPrice if provided
    if (avgBuyPrice !== undefined && !isNaN(avgBuyPrice)) {
      assetData.avgBuyPrice = avgBuyPrice;
    }

    const addAssetResponse = await portfolioStore.addAsset(trimmedAssetId, assetData);
    if (isError(addAssetResponse)) {
      setErrorMessage(`Failed to add asset: ${addAssetResponse.message}. Please check the asset ID.`);
    } else {
      clearForm();
      onClose();
    }
  };

  const handleConflictSuccess = () => {
    clearForm();
    onClose();
  };

  return (
    <>
      <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add Asset to "{bucketName}"</DialogTitle>
        <DialogContent>
          <div className="add-asset-modal">
            <div className="add-asset-modal__form-group">
              <label>Asset ID (CoinGecko ID)</label>
              <Input
                value={assetId}
                onChange={setAssetId}
                placeholder="e.g., bitcoin, ethereum, chainlink"
              />
            </div>
            <div className="add-asset-modal__form-group">
              <label>Amount</label>
              <Input
                value={assetAmount}
                onChange={setAssetAmount}
                placeholder="e.g., 1.5"
              />
            </div>
            <div className="add-asset-modal__form-group">
              <label>Average Buy Price (optional)</label>
              <Input
                value={assetAvgBuyPrice}
                onChange={setAssetAvgBuyPrice}
                placeholder="e.g., 25000"
              />
            </div>
            {errorMessage && (
              <div className="add-asset-modal__error">{errorMessage}</div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={addAsset}>
            Add Asset
          </Button>
        </DialogActions>
      </Dialog>

      {conflictData && (
        <AssetConflictModal
          conflictData={conflictData}
          onClose={() => setConflictData(null)}
          onSuccess={handleConflictSuccess}
        />
      )}
    </>
  );
});
